import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const AdminControls = ({ matchData, onUpdateMatch }) => {
  const [showControls, setShowControls] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeAction, setActiveAction] = useState(null);

  const handleMatchAction = async (action) => {
    setActiveAction(action);
    setIsUpdating(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      switch (action) {
        case 'cancel':
          onUpdateMatch?.(prev => ({
            ...prev,
            status: 'cancelled'
          }));
          alert('Pelada cancelada com sucesso');
          break;
        
        case 'confirm':
          onUpdateMatch?.(prev => ({
            ...prev,
            status: 'confirmed'
          }));
          alert('Pelada confirmada com sucesso');
          break;
        
        case 'postpone': alert('Reagendamento iniciado - jogadores serão notificados');
          break;
        
        case 'duplicate': alert('Nova pelada criada baseada nesta');
          break;
        
        case 'notify':
          alert('Notificação enviada para todos os participantes');
          break;
        
        case 'export':
          alert('Relatório exportado com sucesso');
          break;
        
        default:
          console.log(`Action ${action} executed`);
      }
    } catch (error) {
      console.error('Error executing action:', error);
      alert('Erro ao executar ação. Tente novamente.');
    } finally {
      setIsUpdating(false);
      setActiveAction(null);
    }
  };

  const ControlButton = ({ 
    icon, 
    label, 
    variant = "outline", 
    color = "", 
    action, 
    disabled = false 
  }) => (
    <Button
      variant={variant}
      size="sm"
      iconName={icon}
      iconPosition="left"
      onClick={() => handleMatchAction(action)}
      loading={isUpdating && activeAction === action}
      disabled={disabled || (isUpdating && activeAction !== action)}
      className={`flex-1 ${color}`}
    >
      {isUpdating && activeAction === action ? 'Processando...' : label}
    </Button>
  );

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Settings" size={16} className="text-primary" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-card-foreground">
              Controles de Administrador
            </h3>
            <p className="text-xs text-muted-foreground">
              Gerencie sua pelada
            </p>
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          iconName={showControls ? 'ChevronUp' : 'ChevronDown'}
          onClick={() => setShowControls(!showControls)}
        />
      </div>

      {/* Quick Actions (Always Visible) */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <ControlButton
          icon="UserPlus"
          label="Convidar"
          action="invite"
          color="text-success hover:bg-success/10 border-success/20"
        />
        
        <ControlButton
          icon="Send"
          label="Notificar"
          action="notify"
          color="text-info hover:bg-info/10 border-info/20"
        />
      </div>

      {/* Match Status Controls */}
      <div className="p-3 bg-muted/30 rounded-lg mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-card-foreground">
            Status da Pelada
          </span>
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            matchData?.status === 'confirmed' ?'bg-success/10 text-success' 
              : matchData?.status === 'pending' ?'bg-warning/10 text-warning'
              : matchData?.status === 'cancelled' ?'bg-error/10 text-error' :'bg-muted text-muted-foreground'
          }`}>
            {matchData?.status === 'confirmed' && 'Confirmada'}
            {matchData?.status === 'pending' && 'Pendente'}
            {matchData?.status === 'cancelled' && 'Cancelada'}
          </span>
        </div>
        
        <div className="flex space-x-2">
          {matchData?.status !== 'confirmed' && (
            <ControlButton
              icon="CheckCircle"
              label="Confirmar"
              variant="default"
              action="confirm"
              color="bg-success hover:bg-success/90 text-success-foreground"
            />
          )}
          
          {matchData?.status !== 'cancelled' && (
            <ControlButton
              icon="XCircle"
              label="Cancelar"
              variant="outline"
              action="cancel"
              color="text-error hover:bg-error/10 border-error/20"
            />
          )}
        </div>
      </div>

      {/* Detailed Controls */}
      {showControls && (
        <div className="space-y-4 pt-4 border-t border-border">
          {/* Player Management */}
          <div>
            <h4 className="font-medium text-card-foreground mb-3 flex items-center">
              <Icon name="Users" size={16} className="mr-2 text-primary" />
              Gerenciar Jogadores
            </h4>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <ControlButton
                icon="UserPlus"
                label="Adicionar Jogador"
                action="add-player"
              />
              
              <ControlButton
                icon="UserMinus"
                label="Remover Jogador"
                action="remove-player"
              />
              
              <ControlButton
                icon="Users"
                label="Lista de Espera"
                action="waitlist"
              />
              
              <ControlButton
                icon="Bell"
                label="Enviar Lembretes"
                action="reminders"
              />
            </div>
          </div>

          {/* Payment Management */}
          <div>
            <h4 className="font-medium text-card-foreground mb-3 flex items-center">
              <Icon name="CreditCard" size={16} className="mr-2 text-primary" />
              Controle de Pagamentos
            </h4>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <ControlButton
                icon="DollarSign"
                label="Marcar como Pago"
                action="mark-paid"
              />
              
              <ControlButton
                icon="Receipt"
                label="Gerar Relatório"
                action="payment-report"
              />
              
              <ControlButton
                icon="Send"
                label="Cobrar Pendentes"
                action="payment-reminder"
              />
              
              <ControlButton
                icon="RefreshCw"
                label="Atualizar Valores"
                action="update-prices"
              />
            </div>
          </div>

          {/* Match Management */}
          <div>
            <h4 className="font-medium text-card-foreground mb-3 flex items-center">
              <Icon name="Calendar" size={16} className="mr-2 text-primary" />
              Gerenciar Pelada
            </h4>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <ControlButton
                icon="Edit"
                label="Editar Detalhes"
                action="edit"
              />
              
              <ControlButton
                icon="Clock"
                label="Reagendar"
                action="postpone"
                color="text-warning hover:bg-warning/10 border-warning/20"
              />
              
              <ControlButton
                icon="Copy"
                label="Duplicar Pelada"
                action="duplicate"
              />
              
              <ControlButton
                icon="MapPin"
                label="Alterar Local"
                action="change-venue"
              />
            </div>
          </div>

          {/* Communication */}
          <div>
            <h4 className="font-medium text-card-foreground mb-3 flex items-center">
              <Icon name="MessageCircle" size={16} className="mr-2 text-primary" />
              Comunicação
            </h4>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <ControlButton
                icon="Send"
                label="Mensagem Geral"
                action="broadcast"
              />
              
              <ControlButton
                icon="Phone"
                label="Contatos de Emergência"
                action="emergency-contacts"
              />
              
              <ControlButton
                icon="Share2"
                label="Compartilhar Pelada"
                action="share"
              />
              
              <ControlButton
                icon="Bell"
                label="Push Notification"
                action="push-notification"
              />
            </div>
          </div>

          {/* Reports and Analytics */}
          <div>
            <h4 className="font-medium text-card-foreground mb-3 flex items-center">
              <Icon name="BarChart3" size={16} className="mr-2 text-primary" />
              Relatórios
            </h4>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <ControlButton
                icon="Download"
                label="Exportar Lista"
                action="export"
              />
              
              <ControlButton
                icon="FileText"
                label="Relatório Completo"
                action="full-report"
              />
              
              <ControlButton
                icon="TrendingUp"
                label="Análise de Presença"
                action="attendance-analysis"
              />
              
              <ControlButton
                icon="Star"
                label="Avaliações"
                action="ratings"
              />
            </div>
          </div>

          {/* Emergency Actions */}
          <div className="p-3 bg-error/5 border border-error/20 rounded-lg">
            <h4 className="font-medium text-error mb-2 flex items-center">
              <Icon name="AlertTriangle" size={16} className="mr-2" />
              Ações de Emergência
            </h4>
            <p className="text-xs text-muted-foreground mb-3">
              Use apenas em situações excepcionais
            </p>
            
            <div className="flex space-x-2">
              <ControlButton
                icon="Ban"
                label="Suspender Pelada"
                variant="outline"
                action="suspend"
                color="text-error hover:bg-error/10 border-error/20"
              />
              
              <ControlButton
                icon="AlertOctagon"
                label="Emergência"
                variant="outline"
                action="emergency"
                color="text-error hover:bg-error/10 border-error/20"
              />
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats for Admin */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-lg font-bold text-card-foreground">
              {matchData?.confirmedPlayers?.length || 0}
            </div>
            <div className="text-xs text-muted-foreground">
              Confirmados
            </div>
          </div>
          
          <div>
            <div className="text-lg font-bold text-warning">
              {matchData?.pendingPlayers?.length || 0}
            </div>
            <div className="text-xs text-muted-foreground">
              Pendentes
            </div>
          </div>
          
          <div>
            <div className="text-lg font-bold text-success">
              {matchData?.paymentInfo ? Math.round((matchData?.paymentInfo?.paidAmount / matchData?.paymentInfo?.totalAmount) * 100) : 0}%
            </div>
            <div className="text-xs text-muted-foreground">
              Pagaram
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminControls;